<template>
    <div>
        <div class="pg-head">
            <div class="pg-ico"><span class="mdi mdi-account-multiple-plus"></span></div>
            <div>
                <div class="id-h1">{{ $t('groupbooking.title') }}</div>
                <div class="pg-sub">{{ $t('groupbooking.subtitle') }}</div>
            </div>
        </div>

        <!-- always-on reminder: book ≥1 day ahead so coaches can plan; same-day open until 08:00 -->
        <div class="reserve-notice">
            <span class="mdi mdi-bell-ring-outline"></span>
            <div>
                <div>{{ $t('reservation.advanceNotice') }}</div>
                <div class="t-cap">{{ $t('reservation.advanceNoticeCutoff') }}</div>
            </div>
        </div>

        <!-- STEP 1 — เลือกประเภทคลาส (จากคอร์สของลูกในครอบครัว) -->
        <div class="scard" style="padding:18px;margin-bottom:16px">
            <div class="step-head"><span class="step-num">1</span> {{ $t('groupbooking.stepCourse') }}</div>
            <div v-if="loadingMembers" class="row" style="gap:10px">
                <div class="id-skel" style="height:56px;width:160px"></div>
                <div class="id-skel" style="height:56px;width:160px"></div>
            </div>
            <div v-else-if="!courseOptions.length" class="reserve-empty">
                <span class="mdi mdi-book-off-outline"></span>
                <div>{{ $t('home.errorNoCourse') }}</div>
            </div>
            <div v-else class="row" style="gap:10px;flex-wrap:wrap">
                <button v-for="c in courseOptions" :key="c.courseid" type="button"
                    class="gr-chip" :class="{ on: selectedCourseId === c.courseid }"
                    @click="selectCourse(c.courseid)">
                    <span class="strong">{{ c.coursename }}</span>
                    <span class="t-cap">{{ $t('groupbooking.kidsEligible', { n: c.eligible }) }}</span>
                </button>
            </div>
        </div>

        <div class="grid2 grid2-booking">
            <!-- STEP 2 — เลือกวันที่ -->
            <div class="scard" style="padding:18px;max-width:340px;width:100%;align-self:start">
                <div class="step-head" :class="{ 'is-muted': !selectedCourseId }"><span class="step-num">2</span> {{ $t('groupbooking.stepDate') }}</div>
                <div v-if="!selectedCourseId" class="reserve-empty">
                    <span class="mdi mdi-gesture-tap"></span>
                    <div>{{ $t('groupbooking.pickCourseFirst') }}</div>
                </div>
                <id-calendar v-else
                    :model-value="date ? (date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate()) : ''"
                    :holiday-keys="holidays.map(h => h.year() + '-' + (h.month() + 1) + '-' + h.date())"
                    :min-date="minBookDate"
                    :past-reason="$t('calendar.tooltipBookingClosed')"
                    @update:model-value="key => { const p = key.split('-'); date = new Date(+p[0], +p[1] - 1, +p[2]); selectDate(); }">
                </id-calendar>
            </div>

            <!-- STEP 3 — เลือกรอบเวลา -->
            <div class="scard" style="padding:18px;align-self:start">
                <div class="step-head" :class="{ 'is-muted': !date || !selectedCourseId }"><span class="step-num">3</span> {{ $t('groupbooking.stepTime') }}</div>
                <div v-if="!selectedCourseId || !date" class="reserve-empty">
                    <span class="mdi mdi-calendar-cursor"></span>
                    <div>{{ $t('groupbooking.pickCourseFirst') }}</div>
                </div>
                <div v-else>
                    <div class="reserve-datebar">
                        <span class="mdi mdi-calendar-check"></span> {{ $t('reservation.slotsOf') }} <b>{{ format_date(date) }}</b>
                    </div>
                    <div v-if="loadingSlots" class="cards3" style="grid-template-columns:1fr">
                        <div v-for="i in 2" :key="'sk' + i" class="scard" style="padding:16px">
                            <div class="id-skel" style="height:22px;width:52%;margin-bottom:14px"></div>
                            <div class="id-skel" style="height:42px"></div>
                        </div>
                    </div>
                    <div v-else-if="!classtimesData.length" class="reserve-empty">
                        <span class="mdi mdi-clock-alert-outline"></span>
                        <div>{{ $t('reservation.noClassTime') }}</div>
                    </div>
                    <div v-else class="cards3 id-fade-in" :key="SQLDate(date)" style="grid-template-columns:1fr">
                        <div v-for="s in classtimesData" :key="s.classid" class="scard" style="padding:16px">
                            <div class="row" style="gap:8px;margin-bottom:8px">
                                <span class="mdi mdi-clock-outline" style="color:var(--c-primary);font-size:20px"></span>
                                <span class="strong" style="font-size:18px">{{ s.classtime }}</span>
                                <div style="flex:1"></div>
                                <span class="badge" :class="s.available <= 0 ? 'badge-error' : 'badge-success'">{{ s.disabled ? $t('reservation.unavailable') : (s.available <= 0 ? $t('reservation.full') : $t('reservation.available', { n: s.available })) }}</span>
                            </div>
                            <div v-if="s.disabled" class="t-cap" style="margin-bottom:14px">{{ $t('reservation.unavailable') + (s.description ? ' · ' + s.description : '') }}</div>
                            <button type="button" class="id-btn" style="width:100%"
                                :class="s.available <= 0 ? 'id-btn-ghost' : (classtimeSelect && classtimeSelect.classid === s.classid ? 'id-btn-primary' : 'id-btn-ghost')"
                                :disabled="s.available <= 0"
                                @click="selectSlot(s)">
                                <span class="mdi" :class="s.disabled ? 'mdi-calendar-lock' : (classtimeSelect && classtimeSelect.classid === s.classid ? 'mdi-check-circle' : 'mdi-clock-outline')"></span>
                                {{ s.disabled ? $t('reservation.unavailable') : (classtimeSelect && classtimeSelect.classid === s.classid ? $t('groupbooking.selectedSlot') : $t('groupbooking.selectThisSlot')) }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- STEP 4 — เลือกลูกที่จะจอง -->
        <div class="scard" style="padding:18px;margin-top:16px">
            <div class="step-head" :class="{ 'is-muted': !classtimeSelect }"><span class="step-num">4</span> {{ $t('groupbooking.stepKids') }}</div>
            <div v-if="!classtimeSelect" class="reserve-empty">
                <span class="mdi mdi-account-multiple-outline"></span>
                <div>{{ $t('groupbooking.pickSlotFirst') }}</div>
            </div>
            <template v-else>
                <div v-if="checkingDup" class="cards3" style="grid-template-columns:1fr">
                    <div v-for="i in 2" :key="'dk' + i" class="id-skel" style="height:56px"></div>
                </div>
                <template v-else>
                    <div v-for="m in courseMembers" :key="m.studentid"
                        class="gr-kid" :class="{ on: !!selected[m.studentid], off: !!kidBlockReason(m) }"
                        @click="toggleKid(m)">
                        <span class="id-check" :class="{ on: !!selected[m.studentid] }"><span class="mdi mdi-check"></span></span>
                        <div style="flex:1;min-width:0">
                            <div class="strong" style="font-size:15px">{{ m.fullname }}</div>
                            <div class="t-cap">
                                {{ m.coursename }} ·
                                <template v-if="m.coursetype === 'Monthly'">{{ $t('groupbooking.monthly') }}</template>
                                <template v-else>{{ $t('groupbooking.remainN', { n: m.remaining }) }}</template>
                            </div>
                        </div>
                        <span v-if="kidBlockReason(m)" class="badge badge-error">{{ kidBlockReason(m) }}</span>
                    </div>
                    <div v-for="m in otherMembers" :key="m.studentid" class="gr-kid off">
                        <span class="id-check"><span class="mdi mdi-check"></span></span>
                        <div style="flex:1;min-width:0">
                            <div class="strong" style="font-size:15px;color:var(--c-text-muted)">{{ m.fullname }}</div>
                            <div class="t-cap">{{ m.coursename || $t('home.noCourse') }}</div>
                        </div>
                        <span class="badge badge-warning">{{ $t('groupbooking.notThisCourse') }}</span>
                    </div>

                    <div class="gr-summary" :class="{ 'is-over': overSeat }">
                        <span class="mdi mdi-account-multiple"></span>
                        {{ $t('groupbooking.summary', { n: selectedCount, seats: seatCap }) }}
                        <span v-if="overSeat" class="strong" style="color:var(--c-error)"> — {{ $t('groupbooking.seatShort') }}</span>
                    </div>
                    <button type="button" class="id-btn id-btn-primary" style="width:100%;margin-top:12px"
                        :disabled="selectedCount === 0 || overSeat"
                        @click="openConfirm">
                        <span class="mdi mdi-star-shooting"></span> {{ $t('groupbooking.bookBtn', { n: selectedCount }) }}
                    </button>
                </template>
            </template>
        </div>

        <!-- ยืนยันการจอง -->
        <id-modal v-model="questionDialog" size="md" icon="mdi-calendar-check" :title="$t('groupbooking.confirmTitle', { n: selectedCount })" persistent>
            <div class="t-cap" style="margin-bottom:12px">
                {{ selectedCourseName }} · {{ format_date(date) }} · {{ classtimeSelect ? classtimeSelect.classtime : '' }}
            </div>
            <div v-for="m in selectedMembers" :key="'cf' + m.studentid" class="gr-confirm-row">
                <div class="row" style="gap:8px">
                    <span class="strong" style="flex:1">{{ m.nickname }}</span>
                    <span class="t-cap">
                        <template v-if="m.coursetype === 'Monthly'">{{ $t('groupbooking.monthly') }}</template>
                        <template v-else>{{ $t('groupbooking.afterRemain', { a: m.remaining, b: m.remaining - referNeed(m.courserefer) }) }}</template>
                    </span>
                </div>
                <div class="row" style="gap:8px;margin-top:6px;cursor:pointer" @click="blind[m.studentid] = !blind[m.studentid]">
                    <span class="idsw" :class="{ on: blind[m.studentid] }"><span class="track"></span><span class="thumb"></span></span>
                    <span class="t-cap">{{ $t('reservation.blindName') }}</span>
                </div>
            </div>
            <div class="t-cap" style="margin-top:12px">{{ $t('groupbooking.allOrNothingNote') }}</div>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="questionDialog = false">{{ $t('btn.cancel') }}</button>
                <button class="id-btn id-btn-primary" :disabled="submitting" @click="submitGroupBooking">
                    <span class="mdi mdi-check"></span> {{ $t('reservation.confirmBtn') }}</button>
            </template>
        </id-modal>
    </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { formatFullDate, toDate } from '@/utils/locale'
export default {
    name: 'GroupReservation',
    data() {
        return {
            members: [],
            loadingMembers: false,
            // ล็อกปุ่มยืนยันระหว่างส่งคำขอ (กันกดซ้ำ) — เหมือน Reservation.vue
            submitting: false,
            selectedCourseId: null,
            date: null,
            holidays: [],
            classtimesData: [],
            loadingSlots: false,
            classtimeSelect: null,
            selected: {},   // studentid -> true
            blind: {},      // studentid -> true (ปิดบังชื่อ รายคน)
            dupMap: {},     // studentid -> true = มีจองในวัน/รอบนั้นแล้ว (เช็คจาก /checkDuplicateReservation)
            checkingDup: false,
            questionDialog: false,
        }
    },
    async created() {
        await this.fetchHolidays();
        this.date = new Date();
        this.date.setDate(this.date.getDate() + 1);
        await this.getMembers();
        // ถ้ามีคอร์สเดียว เลือกให้เลย
        if (this.courseOptions.length === 1) this.selectCourse(this.courseOptions[0].courseid);
    },
    computed: {
        // customer จองได้ตั้งแต่พรุ่งนี้เป็นต้นไป ยกเว้นก่อน 08:00 ยังจองคลาสของวันนี้ได้
        // (same-day cutoff — เหมือน Reservation.vue, backend เช็คซ้ำอีกชั้น)
        minBookDate() {
            const d = new Date();
            if (d.getHours() >= 8) d.setDate(d.getDate() + 1);
            d.setHours(0, 0, 0, 0);
            return d;
        },
        // ประเภทคลาส = คอร์สที่ไม่ซ้ำกันของลูกในครอบครัว (เฉพาะคนที่มีคอร์ส)
        courseOptions() {
            const map = {};
            this.members.forEach(m => {
                if (!m.courserefer || !m.courseid) return;
                if (!map[m.courseid]) map[m.courseid] = { courseid: m.courseid, coursename: m.coursename || m.course_shortname, eligible: 0 };
                if (this.isBookable(m)) map[m.courseid].eligible += 1;
            });
            return Object.values(map);
        },
        selectedCourseName() {
            const c = this.courseOptions.find(c => c.courseid === this.selectedCourseId);
            return c ? c.coursename : '';
        },
        courseMembers() {
            return this.members.filter(m => m.courserefer && m.courseid === this.selectedCourseId);
        },
        otherMembers() {
            return this.members.filter(m => !m.courserefer || m.courseid !== this.selectedCourseId);
        },
        selectedMembers() {
            return this.courseMembers.filter(m => this.selected[m.studentid]);
        },
        selectedCount() {
            return this.selectedMembers.length;
        },
        seatCap() {
            return this.classtimeSelect ? this.classtimeSelect.available : 0;
        },
        overSeat() {
            return this.selectedCount > this.seatCap;
        },
    },
    methods: {
        // จองได้ไหมแบบพื้นฐาน (ใช้นับบน chip): มีคอร์ส ไม่หมดอายุ และ (รายเดือน หรือ เหลือครั้ง > 0)
        isBookable(m) {
            if (!m.courserefer) return false;
            if (m.expiredate && new Date(m.expiredate) < new Date()) return false;
            if (m.coursetype !== 'Monthly' && (m.remaining == null || m.remaining <= 0)) return false;
            return true;
        },
        // จำนวนครั้งที่กลุ่มที่เลือกอยู่จะใช้จาก courserefer นี้ (คอร์สที่แชร์กัน)
        referNeed(courserefer) {
            return this.selectedMembers.filter(m => m.courserefer === courserefer).length;
        },
        // เหตุผลที่กดเลือกไม่ได้ (คืน null = เลือกได้) — กันตั้งแต่ front ก่อนถึง backend
        kidBlockReason(m) {
            if (this.selected[m.studentid]) return null; // ที่เลือกแล้ว กดออกได้เสมอ
            if (m.expiredate && new Date(m.expiredate) < new Date()) return this.$t('groupbooking.expired');
            if (m.expiredate && this.date && moment(this.date).isAfter(moment(m.expiredate), 'day')) return this.$t('groupbooking.expired');
            if (m.coursetype !== 'Monthly' && (m.remaining == null || m.remaining <= 0)) return this.$t('groupbooking.usedUp');
            if (this.dupMap[m.studentid]) return this.$t('groupbooking.alreadyBooked');
            // คอร์สนับครั้งที่แชร์กัน: ครั้งคงเหลือรวมต้องพอสำหรับทุกคนที่เลือกใน courserefer เดียวกัน
            if (m.coursetype !== 'Monthly' && this.referNeed(m.courserefer) + 1 > m.remaining) return this.$t('groupbooking.sharedShort');
            if (this.seatCap && this.selectedCount + 1 > this.seatCap) return this.$t('groupbooking.seatShort');
            return null;
        },
        toggleKid(m) {
            if (this.selected[m.studentid]) {
                delete this.selected[m.studentid];
                return;
            }
            if (this.kidBlockReason(m)) return;
            this.selected[m.studentid] = true;
        },
        selectCourse(courseid) {
            if (this.selectedCourseId === courseid) return;
            this.selectedCourseId = courseid;
            this.classtimeSelect = null;
            this.selected = {};
            this.dupMap = {};
            if (this.date) this.getClassTime();
        },
        async selectDate() {
            this.classtimeSelect = null;
            this.dupMap = {};
            await this.getClassTime();
        },
        async selectSlot(s) {
            this.classtimeSelect = s;
            await this.checkDuplicates();
            // ตัดคนที่เพิ่งรู้ว่าจองซ้ำออกจากที่เลือกไว้
            Object.keys(this.selected).forEach(id => { if (this.dupMap[id]) delete this.selected[id]; });
        },
        async getMembers() {
            this.loadingMembers = true;
            const token = this.$store.getters.getToken;
            const user = JSON.parse(localStorage.getItem('userdata'))
            try {
                const response = await axios.post(this.baseURL + '/getFamilyMember',
                    { familyid: user.familyid },
                    { headers: { Authorization: `Bearer ${token}` } });
                if (response.data.success) this.members = response.data.results || [];
            } catch (error) {
                this.$emit('onErrorHandler', error.message);
            } finally {
                this.loadingMembers = false;
            }
        },
        async fetchHolidays() {
            try {
                const token = this.$store.getters.getToken;
                const response = await axios.get(this.baseURL + '/holidaysList', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.success) {
                    this.holidays = response.data.holidays.map(date => moment(date).startOf('day'));
                }
            } catch (error) {
                this.$emit('onErrorHandler', error.message);
            }
        },
        async getClassTime() {
            if (!this.selectedCourseId || !this.date) return;
            const req = {
                classdate: this.SQLDate(this.date),
                classday: toDate(this.date).toLocaleDateString('en-US', { weekday: 'long' }),
                courseid: this.selectedCourseId
            };
            this.loadingSlots = true;
            const t0 = Date.now();
            const token = this.$store.getters.getToken;
            try {
                const response = await axios.post(this.baseURL + '/getClassTime', req, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                this.classtimesData = response.data.success && response.data.results.length ? response.data.results : [];
            } catch (error) {
                this.$emit('onErrorHandler', error.message);
            } finally {
                await this.$minLoad(t0);
                this.loadingSlots = false;
            }
        },
        // เช็คจองซ้ำรายคนล่วงหน้า (endpoint เดิม) — backend จะเช็คซ้ำอีกชั้นตอนบันทึกอยู่ดี
        async checkDuplicates() {
            if (!this.classtimeSelect) return;
            this.checkingDup = true;
            const token = this.$store.getters.getToken;
            const dup = {};
            await Promise.all(this.courseMembers.map(async m => {
                try {
                    const response = await axios.post(this.baseURL + '/checkDuplicateReservation', {
                        studentid: m.studentid,
                        classdate: this.SQLDate(this.date),
                        classtime: this.classtimeSelect.classtime,
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    if (!response.data.success) dup[m.studentid] = true;
                } catch (e) { /* เช็คไม่ได้ = ไม่บล็อกที่ front, backend กันให้อยู่แล้ว */ }
            }));
            this.dupMap = dup;
            this.checkingDup = false;
        },
        openConfirm() {
            if (!this.date || this.date < this.minBookDate) {
                this.$emit('onErrorHandler', this.$t('reservation.bookCutoff'));
                return;
            }
            if (this.selectedCount === 0 || this.overSeat) return;
            this.questionDialog = true;
        },
        async submitGroupBooking() {
            if (this.submitting) return;   // กันกดยืนยันซ้ำ
            this.submitting = true;
            this.questionDialog = false;
            this.$emit('onLoading', true);
            const token = this.$store.getters.getToken;
            try {
                const response = await axios.post(this.baseURL + '/addGroupBookingByCustomer', {
                    courseid: this.selectedCourseId,
                    classid: this.classtimeSelect.classid,
                    classday: toDate(this.date).toLocaleDateString('en-US', { weekday: 'long' }),
                    classdate: this.SQLDate(this.date),
                    classtime: this.classtimeSelect.classtime,
                    bookings: this.selectedMembers.map(m => ({
                        studentid: m.studentid,
                        blindflag: this.blind[m.studentid] ? 1 : 0,
                    })),
                }, { headers: { Authorization: `Bearer ${token}` } });
                if (response.data.success) {
                    this.$emit('onInfoHandler', this.$t('groupbooking.successMsg', { n: this.selectedCount }));
                    this.$emit('onSuccessHandler', 'home');
                } else {
                    // all-or-nothing: backend บอกเหตุผล/รายชื่อคนที่ติด — โหลดข้อมูลใหม่ให้เห็นสถานะล่าสุด
                    this.$emit('onErrorHandler', response.data.message || 'Booking failed');
                    await this.getMembers();
                    await this.getClassTime();
                    if (this.classtimeSelect) await this.checkDuplicates();
                }
            } catch (error) {
                this.$emit('onErrorHandler', error.message || 'Booking failed');
            } finally {
                this.submitting = false;
                this.$emit('onLoading', false);
            }
        },
        SQLDate(value) {
            if (value) return moment(value).format('YYYYMMDD');
        },
        format_date(value) {
            // full weekday+date in the app language (Buddhist era for Thai); was navigator.language
            return value ? formatFullDate(value) : '';
        },
    },
    watch: {
        date(newDate) {
            if (!newDate) return;
            const isHoliday = this.holidays.some(h => moment(newDate).isSame(h, 'day'));
            if (isHoliday || moment(newDate).day() === 1) {
                const next = new Date(newDate);
                do { next.setDate(next.getDate() + 1); }
                while (this.holidays.some(h => moment(next).isSame(h, 'day')) || moment(next).day() === 1);
                this.date = next;
            }
        }
    },
}
</script>

<style scoped>
/* chip เลือกประเภทคลาส */
.gr-chip {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 10px 16px;
    border: 1.5px solid var(--c-border-strong);
    border-radius: var(--radius-md);
    background: var(--c-surface);
    cursor: pointer;
    min-width: 150px;
    text-align: left;
}
.gr-chip.on {
    border-color: var(--c-primary);
    background: var(--c-primary-soft, var(--c-surface-2));
}
.gr-chip .strong { color: var(--c-text-heading); }

/* แถวรายชื่อลูก */
.gr-kid {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-md);
    margin-bottom: 8px;
    cursor: pointer;
    background: var(--c-surface);
}
.gr-kid.on { border-color: var(--c-primary); }
.gr-kid.off { cursor: not-allowed; background: var(--c-surface-2); }

/* แถบสรุปจำนวนที่เลือก */
.gr-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 10px 14px;
    border-radius: var(--radius-md);
    background: var(--c-surface-2);
    color: var(--c-text-heading);
    font-size: 14px;
}
.gr-summary.is-over { background: var(--c-error-soft, var(--c-surface-2)); }
.gr-summary .mdi { color: var(--c-primary); }

/* แถวยืนยันรายคนใน modal */
.gr-confirm-row {
    border: 1px solid var(--c-border);
    border-radius: var(--radius-md);
    padding: 10px 12px;
    margin-bottom: 8px;
    background: var(--c-surface-2);
}
</style>
